import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Bare client for refresh only — no auth interceptors (avoids recursion).
const bareAuthClient = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

const decodeJwtPayload = (token) => {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        const payload = JSON.parse(window.atob(padded));
        return payload && typeof payload === 'object' ? payload : null;
    } catch (e) {
        return null;
    }
};

const isAuthTokenErrorMessage = (message = '') => {
    const normalized = String(message || '').toLowerCase().trim();
    if (!normalized) return false;
    return (
        normalized.includes('no token') ||
        normalized.includes('token is not valid') ||
        normalized.includes('authorization denied') ||
        normalized.includes('token expired') ||
        normalized.includes('session expired') ||
        normalized.includes('jwt expired') ||
        normalized.includes('invalid token')
    );
};

const isRefreshUrl = (url = '') => String(url || '').includes('/auth/refresh-token');

// Refresh when within 2 hours of expiry so long form sessions stay alive.
const TOKEN_REFRESH_THRESHOLD_MS = 2 * 60 * 60 * 1000;
const TOKEN_REFRESH_COOLDOWN_MS = 30 * 1000;
let refreshInFlight = null;
let lastRefreshAttemptAt = 0;
let sessionLogoutInProgress = false;

const applyRefreshedToken = (token) => {
    if (!token) return;
    localStorage.setItem('token', token);
    window.dispatchEvent(new CustomEvent('authTokenRefreshed', { detail: { token } }));
};

const createNetworkRefreshError = () => {
    const err = new Error('AUTH_REFRESH_NETWORK');
    err.isNetworkError = true;
    return err;
};

// Force a token refresh right now (deduped via refreshInFlight).
// Returns the new token string on success, null if auth definitively failed,
// or throws if the failure looks like a transient network error (do not logout).
const refreshAuthToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        try {
            const response = await bareAuthClient.post('/auth/refresh-token', {}, {
                headers: { 'x-auth-token': token },
            });
            const newToken = response.data?.token;
            if (newToken) {
                lastRefreshAttemptAt = Date.now();
                applyRefreshedToken(newToken);
                return newToken;
            }
            return null;
        } catch (e) {
            // No HTTP response => network/CORS/server down. Keep the session.
            if (!e.response) {
                throw createNetworkRefreshError();
            }
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
};

/**
 * Proactively refresh when the JWT is near (or past) expiry.
 * @param {{ force?: boolean }} [options] force=true refreshes even if not near expiry
 */
export const maybeRefreshAuthToken = async (options = {}) => {
    const { force = false } = options;
    const token = localStorage.getItem('token');
    if (!token) return null;

    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return token;

    const msUntilExpiry = payload.exp * 1000 - Date.now();
    // A recently-expired token can still be renewed within the server's grace window.
    if (!force && msUntilExpiry > TOKEN_REFRESH_THRESHOLD_MS) {
        return token;
    }

    const now = Date.now();
    if (refreshInFlight) return refreshInFlight;

    // Expired / forced refreshes skip cooldown so submit never sits on a dead token
    // after a failed attempt earlier in the minute.
    const mustRefreshNow = force || msUntilExpiry <= 0;
    if (!mustRefreshNow && now - lastRefreshAttemptAt < TOKEN_REFRESH_COOLDOWN_MS) {
        return token;
    }

    lastRefreshAttemptAt = now;
    try {
        const newToken = await refreshAuthToken();
        return newToken || localStorage.getItem('token') || token;
    } catch (e) {
        if (e?.isNetworkError) {
            // Allow an immediate retry on the next interaction / request.
            lastRefreshAttemptAt = 0;
            return localStorage.getItem('token') || token;
        }
        return localStorage.getItem('token') || token;
    }
};

/** Ensure a usable token before a critical write (forms). Forces refresh if expired/near expiry. */
export const ensureFreshAuthToken = async () => maybeRefreshAuthToken({ force: false });

const forceSessionLogout = () => {
    if (sessionLogoutInProgress) return;
    sessionLogoutInProgress = true;

    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');

    window.dispatchEvent(new CustomEvent('tokenExpired', {
        detail: { message: 'Session expired. Please log in again.' },
    }));

    window.location.href = '/login';
};

/**
 * Attach refresh-before-request + 401 refresh-and-retry to any axios instance.
 * Applied to both the shared `api` client and the default `axios` export so forms
 * that call axios directly still recover instead of logging the user out mid-submit.
 */
const attachStaffAuthInterceptors = (client) => {
    client.interceptors.request.use(
        async (config) => {
            if (isRefreshUrl(config.url)) {
                return config;
            }
            // Always try to renew when expired/near expiry before the request goes out.
            await maybeRefreshAuthToken();
            const token = localStorage.getItem('token');
            if (token) {
                config.headers = config.headers || {};
                config.headers['x-auth-token'] = token;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    client.interceptors.response.use(
        (response) => {
            if (localStorage.getItem('token')) {
                window.dispatchEvent(new CustomEvent('appActivity'));
            }
            return response;
        },
        async (error) => {
            if (!(error.response && error.response.status === 401)) {
                return Promise.reject(error);
            }

            const isPortalPath = window.location.pathname.startsWith('/customer-complaint');
            if (isPortalPath) {
                localStorage.removeItem('portalToken');
                localStorage.removeItem('portalUser');
                window.location.href = '/customer-complaint/login';
                return new Promise(() => {});
            }

            const originalRequest = error.config || {};
            if (isRefreshUrl(originalRequest.url)) {
                // Refresh endpoint itself rejected — session cannot be renewed.
                forceSessionLogout();
                return new Promise(() => {});
            }

            const token = localStorage.getItem('token');
            const backendMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                '';
            const looksLikeAuthFailure = !token || isAuthTokenErrorMessage(backendMessage);

            if (!looksLikeAuthFailure) {
                // Business-rule / permission 401 — keep the session.
                return Promise.reject(error);
            }

            // Transparent recovery: refresh (grace window) and replay the original
            // submit so the user does not lose a filled-out form.
            if (token && !originalRequest._retriedAfterRefresh) {
                originalRequest._retriedAfterRefresh = true;
                try {
                    const newToken = await refreshAuthToken();
                    if (newToken) {
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers['x-auth-token'] = newToken;
                        return client(originalRequest);
                    }
                } catch (refreshErr) {
                    if (refreshErr?.isNetworkError) {
                        // Transient failure — keep the user logged in and let the form show an error.
                        return Promise.reject(error);
                    }
                }
            }

            forceSessionLogout();
            return new Promise(() => {});
        }
    );
};

// Guard against Fast Refresh stacking duplicate interceptors in development.
// Do NOT put a custom header on defaults — that breaks CORS preflight to the API.
try {
    delete api.defaults.headers.common['X-Midtronix-Auth'];
    delete axios.defaults.headers.common['X-Midtronix-Auth'];
    delete api.defaults.headers.common['x-midtronix-auth'];
    delete axios.defaults.headers.common['x-midtronix-auth'];
} catch (e) {
    // ignore
}

if (!axios.__midtronixStaffAuthAttached) {
    axios.__midtronixStaffAuthAttached = true;
    attachStaffAuthInterceptors(api);
    // Cover components that import axios directly instead of the shared api client.
    attachStaffAuthInterceptors(axios);
}

/**
 * fetch() wrapper with the same refresh + one-shot 401 retry behavior as axios.
 * Use for the few call sites that still use fetch instead of the api client.
 */
export async function authFetch(url, options = {}) {
    await maybeRefreshAuthToken();
    const headers = new Headers(options.headers || {});
    const token = localStorage.getItem('token');
    if (token) {
        headers.set('x-auth-token', token);
    }

    const response = await fetch(url, { ...options, headers });
    if (response.status !== 401 || !localStorage.getItem('token')) {
        return response;
    }

    const clonedForMessage = response.clone();
    let backendMessage = '';
    try {
        const body = await clonedForMessage.json();
        backendMessage = body?.error || body?.message || '';
    } catch (e) {
        // ignore non-JSON bodies
    }

    if (backendMessage && !isAuthTokenErrorMessage(backendMessage)) {
        return response;
    }

    try {
        const newToken = await refreshAuthToken();
        if (newToken) {
            headers.set('x-auth-token', newToken);
            return fetch(url, { ...options, headers });
        }
    } catch (refreshErr) {
        if (refreshErr?.isNetworkError) {
            return response;
        }
    }

    forceSessionLogout();
    return new Promise(() => {});
}

const portalApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

portalApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('portalToken');
        if (token) {
            config.headers['x-portal-auth-token'] = token;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

portalApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('portalToken');
            localStorage.removeItem('portalUser');
            if (window.location.pathname.startsWith('/customer-complaint')) {
                window.location.href = '/customer-complaint/login';
                return new Promise(() => {});
            }
        }
        return Promise.reject(error);
    }
);

export const authService = {
    // Company authentication
    companySignup: async (companyData) => {
        const response = await api.post('/company/signup', companyData);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userType', 'company');
        }
        return response.data;
    },

    login: async (credentials) => {
        const response = await api.post('/auth/login', credentials);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userType', response.data.userType);
        }
        return response.data;
    },

    companyLogin: async (credentials) => {
        const response = await api.post('/company/login', credentials);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userType', 'company');
        }
        return response.data;
    },

    // Employee authentication
    employeeLogin: async (credentials) => {
        const response = await api.post('/employees/login', credentials);
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userType', 'employee');
        }
        return response.data;
    },

    // Employee registration
    employeeRegister: async (employeeData) => {
        const response = await api.post('/employees/register', employeeData);
        return response.data;
    },

    // Logout
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        return !!localStorage.getItem('token');
    },

    // Get current user profile
    getCurrentUser: async () => {
        const response = await api.get('/profile');
        return response.data;
    },

    // Get user type
    getUserType: () => {
        return localStorage.getItem('userType');
    }
};

export const portalAuthService = {
    signup: async (data) => {
        const response = await portalApi.post('/customer-complaint/auth/signup', data);
        if (response.data?.token) {
            localStorage.setItem('portalToken', response.data.token);
            if (response.data.user) {
                localStorage.setItem('portalUser', JSON.stringify(response.data.user));
            }
        }
        return response.data;
    },
    login: async (data) => {
        const response = await portalApi.post('/customer-complaint/auth/login', data);
        if (response.data?.token) {
            localStorage.setItem('portalToken', response.data.token);
            if (response.data.user) {
                localStorage.setItem('portalUser', JSON.stringify(response.data.user));
            }
        }
        return response.data;
    },
    me: async () => {
        const response = await portalApi.get('/customer-complaint/auth/me');
        return response.data;
    },
    logout: () => {
        localStorage.removeItem('portalToken');
        localStorage.removeItem('portalUser');
    }
};

export const portalComplaintService = {
    list: async () => {
        const response = await portalApi.get('/customer-complaint/complaints');
        return response.data;
    },
    get: async (id) => {
        const response = await portalApi.get(`/customer-complaint/complaints/${id}`);
        return response.data;
    },
    create: async (data) => {
        const hasFiles = Array.isArray(data.files) && data.files.length > 0;
        if (!hasFiles) {
            const payload = {
                title: data.title,
                description: data.description,
                priority: data.priority,
                impact: data.impact,
                ticketType: data.ticketType,
                category: data.category
            };
            const response = await portalApi.post('/customer-complaint/complaints', payload);
            return response.data;
        }

        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('description', data.description);
        formData.append('priority', data.priority);
        formData.append('impact', data.impact);
        formData.append('ticketType', data.ticketType);
        formData.append('category', data.category);
        data.files.forEach((file) => formData.append('files', file));

        const response = await portalApi.post('/customer-complaint/complaints', formData, {
            transformRequest: [(payload, headers) => {
                if (payload instanceof FormData) {
                    delete headers['Content-Type'];
                }
                return payload;
            }]
        });
        return response.data;
    },
    uploadAttachments: async (id, files = []) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        const response = await portalApi.post(`/customer-complaint/complaints/${id}/attachments`, formData, {
            transformRequest: [(payload, headers) => {
                if (payload instanceof FormData) {
                    delete headers['Content-Type'];
                }
                return payload;
            }]
        });
        return response.data;
    }
};

export const salesService = {
    getSales: async (status, queryParams = {}) => {
        const params = { ...(status ? { status } : {}), ...queryParams };
        const wantsPagination = params.page != null || params.limit != null;
        if (!wantsPagination && params.all == null) {
            params.all = 'true';
        }
        const response = await api.get('/sales', { params });
        return response.data;
    },
    /** Tab list including documents that progressed (SO still listed after it became a DO/invoice). */
    getSalesForTab: async (tab, queryParams = {}) => {
        const params = { tab, all: 'true', ...queryParams };
        const response = await api.get('/sales/tab-list', { params });
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.rows)) return data.rows;
        throw new Error('Invalid sales tab-list response');
    },
    /** Lightweight next document number for create forms: { number }. type: estimate | salesOrder | invoice | purchase */
    getNextDocumentNumber: async (type) => {
        const response = await api.get('/sales/next-document-number', { params: { type } });
        return response.data;
    },
    /** { exists: boolean, id?: number|null }. type: invoice. excludeId skips the sale being edited. */
    checkDocumentNumber: async ({ type, number, excludeId } = {}) => {
        const params = { type, number };
        if (excludeId != null && excludeId !== '') params.excludeId = excludeId;
        const response = await api.get('/sales/check-document-number', { params });
        return response.data;
    },
    /** Sales invoices and delivery orders that include this inventory item (optional dateFrom/dateTo as YYYY-MM-DD). Each row has documentType 'Invoice' | 'Delivery'. */
    getInvoicesForInventoryItem: async (inventoryItemId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const query = queryParams.toString();
        const url = query
            ? `/sales/for-inventory-item/${encodeURIComponent(inventoryItemId)}?${query}`
            : `/sales/for-inventory-item/${encodeURIComponent(inventoryItemId)}`;
        const response = await api.get(url);
        return response.data;
    },
    getSale: async (id) => {
        const response = await api.get(`/sales/${id}`);
        return response.data;
    },
    getSaleActivityLogs: async (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit != null) {
            queryParams.append('limit', params.limit);
        }
        const query = queryParams.toString();
        const url = query
            ? `/sales/${id}/activity-logs?${query}`
            : `/sales/${id}/activity-logs`;
        const response = await api.get(url);
        return response.data;
    },
    createSale: async (data) => {
        // Check if data is FormData (for file uploads)
        const isFormData = data instanceof FormData;
        const config = isFormData ? {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        } : {};
        
        const response = await api.post('/sales', data, config);
        return response.data;
    },
    /** Deduct customer credit memo balance toward an invoice payment. */
    applyCreditMemo: async ({ customerId, amount, invoiceNumber } = {}) => {
        const response = await api.post('/sales/apply-credit-memo', {
            customerId,
            amount,
            invoiceNumber
        });
        return response.data;
    },
    /** Add customer credit memo balance back when a recorded invoice payment is edited. */
    restoreCreditMemo: async ({ customerId, amount, invoiceNumber } = {}) => {
        const response = await api.post('/sales/restore-credit-memo', {
            customerId,
            amount,
            invoiceNumber
        });
        return response.data;
    },
    updateSale: async (id, data) => {
        // Check if data is FormData (for file uploads)
        const isFormData = data instanceof FormData;
        const config = isFormData ? {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        } : {};
        
        const response = await api.put(`/sales/${id}`, data, config);
        return response.data;
    },
    deleteSale: async (id) => {
        const response = await api.delete(`/sales/${id}`);
        return response.data;
    },
    /** Remove signed delivery / invoice document from a sale */
    deleteSignedDeliveryDocument: async (id) => {
        const response = await api.delete(`/sales/${id}/signed-delivery-document`);
        return response.data;
    },
    /** Clone an estimate into a new record with number BASE-Vn; marks other Sent estimates in the same family as Superseded. */
    createEstimateVersion: async (saleId) => {
        const response = await api.post(`/sales/${saleId}/estimate-version`);
        return response.data;
    },
    /** Clone an invoice into a new record with number BASE-Vn (no inventory or payment carry-over). */
    createInvoiceVersion: async (saleId) => {
        const response = await api.post(`/sales/${saleId}/invoice-version`);
        return response.data;
    },
    getDeleteInfo: async (id) => {
        const response = await api.get(`/sales/${id}/delete-info`);
        return response.data;
    },
    returnByStaff: async (id, returnsData) => {
        const response = await api.put(`/sales/${id}/return-by-staff`, returnsData);
        return response.data;
    },
    mergeDeliveryOrdersToInvoice: async (deliveryOrderIds, payload = {}) => {
        const response = await api.post('/sales/merge-delivery-orders-to-invoice', {
            deliveryOrderIds,
            ...payload
        });
        return response.data;
    },
    mergeInvoices: async (invoiceIds, payload = {}) => {
        const response = await api.post('/sales/merge-invoices', {
            invoiceIds,
            ...payload
        });
        return response.data;
    },
    getEligibleDeliveryOrdersToMerge: async (invoiceId) => {
        const response = await api.get(`/sales/${invoiceId}/eligible-delivery-orders-to-merge`);
        return response.data;
    },
    mergeAdditionalDeliveryOrdersIntoInvoice: async (invoiceId, deliveryOrderIds) => {
        const response = await api.post(`/sales/${invoiceId}/merge-additional-delivery-orders`, {
            deliveryOrderIds
        });
        return response.data;
    },
    unmergeDeliveryOrderFromInvoice: async (invoiceId, deliveryOrderId) => {
        const response = await api.post(`/sales/${invoiceId}/unmerge-delivery-order`, {
            deliveryOrderId
        });
        return response.data;
    },
    createPartialDelivery: async (id, data) => {
        const response = await api.post(`/sales/${id}/partial-delivery`, data);
        return response.data;
    },
};

export const posSalesService = {
    getPOSSales: async (params = {}) => {
        const response = await api.get('/pos-sales', { params });
        return response.data;
    },
    getPOSSale: async (id) => {
        const response = await api.get(`/pos-sales/${id}`);
        return response.data;
    },
    createPOSSale: async (data) => {
        const response = await api.post('/pos-sales', data);
        return response.data;
    },
    updatePOSSale: async (id, data) => {
        const response = await api.put(`/pos-sales/${id}`, data);
        return response.data;
    },
    deletePOSSale: async (id) => {
        const response = await api.delete(`/pos-sales/${id}`);
        return response.data;
    },
};

export const topupSalesService = {
    getTopupSales: async (params = {}) => {
        const response = await api.get('/topup-sales', { params });
        return response.data;
    },
    getTopupSale: async (id) => {
        const response = await api.get(`/topup-sales/${id}`);
        return response.data;
    },
    createTopupSale: async (data) => {
        const response = await api.post('/topup-sales', data);
        return response.data;
    },
    deleteTopupSale: async (id) => {
        const response = await api.delete(`/topup-sales/${id}`);
        return response.data;
    },
};

export const purchaseService = {
    /** Purchase invoices that include this inventory item (optional dateFrom/dateTo as YYYY-MM-DD) */
    getInvoicesForInventoryItem: async (inventoryItemId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const query = queryParams.toString();
        const url = query
            ? `/purchases/for-inventory-item/${encodeURIComponent(inventoryItemId)}?${query}`
            : `/purchases/for-inventory-item/${encodeURIComponent(inventoryItemId)}`;
        const response = await api.get(url);
        return response.data;
    },
    getPurchases: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit != null) queryParams.append('limit', params.limit);
        if (params.skip != null) queryParams.append('skip', params.skip);
        if (params.orderStatus) queryParams.append('orderStatus', params.orderStatus);
        if (params.search) queryParams.append('search', params.search);
        if (params.exactInvoiceNo)
            queryParams.append('exactInvoiceNo', params.exactInvoiceNo);
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const queryString = queryParams.toString();
        const url = queryString ? `/purchases?${queryString}` : '/purchases';
        const response = await api.get(url);
        return response.data;
    },
    getPurchaseById: async (id) => {
        const response = await api.get(`/purchases/${id}`);
        return response.data;
    },
    createPurchase: async (data) => {
        const response = await api.post('/purchases', data);
        return response.data;
    },
    createPurchaseWithFile: async (formData) => {
        const response = await api.post('/purchases', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    },
    updatePurchase: async (id, data) => {
        const response = await api.put(`/purchases/${id}`, data);
        return response.data;
    },
    updatePurchaseWithFile: async (id, formData) => {
        const response = await api.put(`/purchases/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    },
    deletePurchase: async (id) => {
        const response = await api.delete(`/purchases/${id}`);
        return response.data;
    },
    validateSerialNumbers: async ({ items, editPurchaseId }) => {
        const response = await api.post('/purchases/validate-serial-numbers', {
            items,
            editPurchaseId: editPurchaseId || undefined,
        });
        return response.data;
    },
};

export const inventoryService = {
    // Get all inventory items
    getInventoryItems: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.search !== undefined && params.search !== '') {
            queryParams.append('search', params.search);
        }
        if (params.categoryId !== undefined && params.categoryId !== '') {
            queryParams.append('categoryId', params.categoryId);
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/inventory?${queryString}` : '/inventory';
        const response = await api.get(url);
        return response.data;
    },

    // Get single inventory item
    getInventoryItem: async (id) => {
        const response = await api.get(`/inventory/${id}`);
        return response.data;
    },

    getInventoryItemActivityLogs: async (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit != null) {
            queryParams.append('limit', params.limit);
        }
        const query = queryParams.toString();
        const url = query
            ? `/inventory/${id}/activity-logs?${query}`
            : `/inventory/${id}/activity-logs`;
        const response = await api.get(url);
        return response.data;
    },

    getItemWiseReport: async (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const query = queryParams.toString();
        const url = query
            ? `/inventory/${encodeURIComponent(id)}/item-wise-report?${query}`
            : `/inventory/${encodeURIComponent(id)}/item-wise-report`;
        const response = await api.get(url);
        return response.data;
    },

    /** Returns { modelNumber: null | { id, name, variantName?, source }, mtxModelNumber: null | { id, name } } */
    checkDuplicateModelNumbers: async ({ modelNumber = '', mtxModelNumber = '' } = {}) => {
        const qs = new URLSearchParams();
        if (modelNumber) qs.append('modelNumber', modelNumber);
        if (mtxModelNumber) qs.append('mtxModelNumber', mtxModelNumber);
        const query = qs.toString();
        const url = query
            ? `/inventory/check-duplicate-model-numbers?${query}`
            : '/inventory/check-duplicate-model-numbers';
        const response = await api.get(url);
        return response.data;
    },

    // Get variant by ID
    getVariant: async (variantId) => {
        const response = await api.get(`/inventory/variant/${variantId}`);
        return response.data;
    },

    // Create new inventory item
    createInventoryItem: async (data) => {
        const response = await api.post('/inventory', data);
        return response.data;
    },

    /** Consume BOM components and create a new manufactured inventory row */
    manufactureInventoryItem: async (data) => {
        const response = await api.post('/inventory/manufacture', data);
        return response.data;
    },

    /** Update metadata + BOM for a manufactured row (PUT /inventory/:id/manufacture) */
    updateManufacturedInventoryItem: async (id, data) => {
        const response = await api.put(`/inventory/${id}/manufacture`, data);
        return response.data;
    },

    // Update inventory item
    updateInventoryItem: async (id, data) => {
        const response = await api.put(`/inventory/${id}`, data);
        return response.data;
    },

    updateInventoryProductOrderLinks: async (id, productOrderLinks) => {
        const response = await api.patch(`/inventory/${id}/product-order-links`, {
            productOrderLinks,
        });
        return response.data;
    },

    // Upload item image (multipart; drop default JSON Content-Type so boundary is set)
    uploadInventoryItemImage: async (id, file) => {
        const formData = new FormData();
        formData.append('image', file);
        const response = await api.post(`/inventory/${id}/image`, formData, {
            transformRequest: [(data, headers) => {
                if (headers && typeof headers.delete === 'function') {
                    headers.delete('Content-Type');
                } else if (headers) {
                    delete headers['Content-Type'];
                }
                return data;
            }]
        });
        return response.data;
    },

    // Remove item image
    removeInventoryItemImage: async (id) => {
        const response = await api.delete(`/inventory/${id}/image`);
        return response.data;
    },

    // Add serial numbers to inventory item
    addSerialNumbers: async (id, serialNumbers) => {
        const response = await api.put(`/inventory/${id}/add-serial-numbers`, { serialNumbers });
        return response.data;
    },

    // Increment inventory quantity (atomic operation)
    incrementQuantity: async (id, quantity) => {
        const response = await api.put(`/inventory/${id}/increment-quantity`, { quantity });
        return response.data;
    },

    // Remove serial numbers from inventory item
    removeSerialNumbers: async (id, serialNumbers) => {
        const response = await api.put(`/inventory/${id}/remove-serial-numbers`, { serialNumbers });
        return response.data;
    },

    // Delete inventory item
    deleteInventoryItem: async (id) => {
        const response = await api.delete(`/inventory/${id}`);
        return response.data;
    },

    // Get inventory analytics
    getInventoryAnalytics: async () => {
        const response = await api.get('/inventory/analytics/total');
        return response.data;
    },

    // Get low quantity items
    getLowQuantityItems: async (threshold = 10) => {
        const response = await api.get(`/inventory/analytics/low-quantity?threshold=${threshold}`);
        return response.data;
    },

    // Get inventory overview
    getInventoryOverview: async () => {
        const response = await api.get('/inventory/analytics/overview');
        return response.data;
    },

    // Remove serial numbers from inventory item (when sold)
    updateItemSerialNumbers: async (itemId, soldSerialNumbers) => {
        const response = await api.put(`/inventory/${itemId}/remove-serial-numbers`, {
            serialNumbers: soldSerialNumbers
        });
        return response.data;
    },

    // Get inventory categories
    getInventoryCategories: async () => {
        const response = await api.get('/inventory/categories');
        return response.data;
    },

    // Create inventory category
    createInventoryCategory: async (data) => {
        const response = await api.post('/inventory/categories', data);
        return response.data;
    },

    // Delete inventory category
    deleteInventoryCategory: async (id) => {
        const response = await api.delete(`/inventory/categories/${id}`);
        return response.data;
    },

    // Get items available for POS
    getPOSItems: async () => {
        const response = await api.get('/inventory/pos');
        return response.data;
    }
};

export const bookkeepingService = {
    // Get all bookkeeping entries
    getBookkeepingEntries: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.chartOfAccount !== undefined && params.chartOfAccount !== '') {
            queryParams.append('chartOfAccount', params.chartOfAccount);
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/bookkeeping?${queryString}` : '/bookkeeping';
        const response = await api.get(url);
        return response.data;
    },

    // Get single bookkeeping entry
    getBookkeepingEntry: async (id) => {
        const response = await api.get(`/bookkeeping/${id}`);
        return response.data;
    },

    // Create new bookkeeping entry
    createBookkeepingEntry: async (data) => {
        const response = await api.post('/bookkeeping', data);
        return response.data;
    },

    // Update bookkeeping entry
    updateBookkeepingEntry: async (id, data) => {
        const response = await api.put(`/bookkeeping/${id}`, data);
        return response.data;
    },

    // Delete bookkeeping entry
    deleteBookkeepingEntry: async (id) => {
        const response = await api.delete(`/bookkeeping/${id}`);
        return response.data;
    }
};

export const dashboardService = {
    // Get all dashboard data
    getDashboardData: async (startDate, endDate) => {
        const response = await api.get('/dashboard', {
            params: { startDate, endDate }
        });
        return response.data;
    }
};

export const returnsService = {
    // Get all returns by status
    getReturns: async (status) => {
        const response = await api.get('/returns', { params: status ? { status } : {} });
        return response.data;
    },

    /** Customer/company returns that include this inventory item (optional dateFrom/dateTo as YYYY-MM-DD). */
    getReturnsForInventoryItem: async (inventoryItemId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const query = queryParams.toString();
        const url = query
            ? `/returns/for-inventory-item/${encodeURIComponent(inventoryItemId)}?${query}`
            : `/returns/for-inventory-item/${encodeURIComponent(inventoryItemId)}`;
        const response = await api.get(url);
        return response.data;
    },
    
    // Get customer returns specifically
    getCustomerReturns: async (status) => {
        const response = await api.get('/returns/customer', { params: status ? { status } : {} });
        return response.data;
    },

    // Create new return request
    createReturn: async (data) => {
        const response = await api.post('/returns', data);
        return response.data;
    },

    // Update return status and details
    updateReturn: async (id, data) => {
        const response = await api.put(`/returns/${id}`, data);
        return response.data;
    },

    // Approve or reject return
    processApproval: async (id, approvalData) => {
        const response = await api.put(`/returns/${id}/approval`, approvalData);
        return response.data;
    },

    // Process return items
    processReturn: async (id, processingData) => {
        const response = await api.put(`/returns/${id}/process`, processingData);
        return response.data;
    },

    // Process refund
    processRefund: async (id, refundData) => {
        const response = await api.put(`/returns/${id}/refund`, refundData);
        return response.data;
    },

    // Delete return
    deleteReturn: async (id) => {
        const response = await api.delete(`/returns/${id}`);
        return response.data;
    }
};

export const companyReturnsService = {
    // Get all company returns by status
    getCompanyReturns: async (status) => {
        const response = await api.get('/returns', { params: { companyReturn: true, ...(status && { status }) } });
        return response.data;
    },

    // Create new company return request
    createCompanyReturn: async (data) => {
        const response = await api.post('/returns', { ...data, companyReturn: true });
        return response.data;
    },

    // Update company return status and details
    updateCompanyReturn: async (id, data) => {
        const response = await api.put(`/returns/${id}`, { ...data, companyReturn: true });
        return response.data;
    },

    // Approve or reject company return
    processCompanyReturnApproval: async (id, approvalData) => {
        const response = await api.put(`/returns/${id}/approval`, approvalData);
        return response.data;
    },

    // Process company return items
    processCompanyReturn: async (id, processingData) => {
        const response = await api.put(`/returns/${id}/process`, processingData);
        return response.data;
    },

    // Process company return refund
    processCompanyReturnRefund: async (id, refundData) => {
        const response = await api.put(`/returns/${id}/refund`, refundData);
        return response.data;
    },

    // Delete company return
    deleteCompanyReturn: async (id) => {
        const response = await api.delete(`/returns/${id}`);
        return response.data;
    }
};

export const attendanceService = {
    getRecords: async (params = {}) => {
        const response = await api.get('/attendance', { params });
        return response.data;
    },

    getSummary: async (params = {}) => {
        const response = await api.get('/attendance/summary', { params });
        return response.data;
    },

    getDepartments: async () => {
        const response = await api.get('/attendance/departments');
        return response.data;
    },

    getImports: async (params = {}) => {
        const response = await api.get('/attendance/imports', { params });
        return response.data;
    },

    importRecords: async (payload) => {
        const response = await api.post('/attendance/import', payload);
        return response.data;
    },

    parseFile: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/attendance/parse', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    importFile: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/attendance/import-file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const salaryPaymentService = {
    // Get all salary payments
    getSalaryPayments: async (params = {}) => {
        const response = await api.get('/salary-payments', { params });
        return response.data;
    },

    // Get single salary payment
    getSalaryPayment: async (id) => {
        const response = await api.get(`/salary-payments/${id}`);
        return response.data;
    },

    // Create new salary payment
    createSalaryPayment: async (data) => {
        const response = await api.post('/salary-payments', data);
        return response.data;
    },

    // Update salary payment
    updateSalaryPayment: async (id, data) => {
        const response = await api.put(`/salary-payments/${id}`, data);
        return response.data;
    },

    // Cancel salary payment
    cancelSalaryPayment: async (id) => {
        const response = await api.delete(`/salary-payments/${id}`);
        return response.data;
    },

    // Get salary payment analytics
    getSalaryAnalytics: async (params = {}) => {
        const response = await api.get('/salary-payments/analytics/summary', { params });
        return response.data;
    }
};

export const todoService = {
    // Get all todos
    getTodos: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.status !== undefined && params.status !== '') {
            queryParams.append('status', params.status);
        }
        if (params.activeOnly === true) {
            queryParams.append('activeOnly', 'true');
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/todos?${queryString}` : '/todos';
        const response = await api.get(url);
        return response.data;
    },

    // Get todos by status
    getTodosByStatus: async (status) => {
        const response = await api.get(`/todos/status/${status}`);
        return response.data;
    },

    // Get my todos (assigned to current user)
    getMyTodos: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.status !== undefined && params.status !== '') {
            queryParams.append('status', params.status);
        }
        if (params.activeOnly === true) {
            queryParams.append('activeOnly', 'true');
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/todos/my-todos?${queryString}` : '/todos/my-todos';
        const response = await api.get(url);
        return response.data;
    },

    // Create new todo
    createTodo: async (data) => {
        const response = await api.post('/todos', data);
        return response.data;
    },

    // Update todo
    updateTodo: async (id, data) => {
        const response = await api.put(`/todos/${id}`, data);
        return response.data;
    },

    // Delete todo
    deleteTodo: async (id) => {
        const response = await api.delete(`/todos/${id}`);
        return response.data;
    },

    // Get todo statistics
    getTodoStats: async () => {
        const response = await api.get('/todos/stats/summary');
        return response.data;
    }
};

export const taskService = {
    getTasks: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.status !== undefined && params.status !== '') {
            queryParams.append('status', params.status);
        }
        if (params.activeOnly === true) {
            queryParams.append('activeOnly', 'true');
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/tasks?${queryString}` : '/tasks';
        const response = await api.get(url);
        return response.data;
    },

    getTasksByStatus: async (status) => {
        const response = await api.get(`/tasks/status/${status}`);
        return response.data;
    },

    getMyTasks: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.status !== undefined && params.status !== '') {
            queryParams.append('status', params.status);
        }
        if (params.activeOnly === true) {
            queryParams.append('activeOnly', 'true');
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/tasks/my-tasks?${queryString}` : '/tasks/my-tasks';
        const response = await api.get(url);
        return response.data;
    },

    createTask: async (data) => {
        const response = await api.post('/tasks', data);
        return response.data;
    },

    updateTask: async (id, data) => {
        const response = await api.put(`/tasks/${id}`, data);
        return response.data;
    },

    deleteTask: async (id) => {
        const response = await api.delete(`/tasks/${id}`);
        return response.data;
    },

    getTaskStats: async () => {
        const response = await api.get('/tasks/stats/summary');
        return response.data;
    }
};

export const jobService = {
    // Get all jobs with filters
    getJobs: async (params = {}) => {
        const response = await api.get('/jobs', { params });
        return response.data;
    },

    getJobStatsSummary: async () => {
        const response = await api.get('/jobs/stats/summary');
        return response.data;
    },

    // Get single job
    getJob: async (id) => {
        const response = await api.get(`/jobs/${id}`);
        return response.data;
    },

    getJobActivityLogs: async (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit != null) {
            queryParams.append('limit', params.limit);
        }
        const query = queryParams.toString();
        const url = query
            ? `/jobs/${id}/activity-logs?${query}`
            : `/jobs/${id}/activity-logs`;
        const response = await api.get(url);
        return response.data;
    },

    // Create new job
    createJob: async (data) => {
        const response = await api.post('/jobs', data);
        return response.data;
    },

    // Update job
    updateJob: async (id, data) => {
        const response = await api.put(`/jobs/${id}`, data);
        return response.data;
    },

    // Delete job
    deleteJob: async (id) => {
        const response = await api.delete(`/jobs/${id}`);
        return response.data;
    },

    getJobDeleteInfo: async (id) => {
        const safe = encodeURIComponent(String(id ?? ''));
        const response = await api.get(`/jobs/${safe}/delete-info`);
        return response.data;
    },

    // Update job status
    updateJobStatus: async (id, status, cancellationReason = null) => {
        const payload = { status };
        if (cancellationReason) {
            payload.cancellationReason = cancellationReason;
        }
        const response = await api.put(`/jobs/${id}/status`, payload);
        return response.data;
    },

    // Get sub-jobs
    getSubJobs: async (id) => {
        const response = await api.get(`/jobs/${id}/sub-jobs`);
        return response.data;
    },

    // Create sub-job
    createSubJob: async (parentId, data) => {
        const response = await api.post(`/jobs/${parentId}/sub-jobs`, data);
        return response.data;
    },

    // Record payment for job
    recordPayment: async (id, paymentData) => {
        const response = await api.put(`/jobs/${id}/payment`, paymentData);
        return response.data;
    },

    // Get progress reports
    getProgressReports: async (id) => {
        const response = await api.get(`/jobs/${id}/progress`);
        return response.data;
    },

    // Add progress report (optional File as third arg — sent as multipart)
    addProgressReport: async (id, data, file) => {
        if (file) {
            const formData = new FormData();
            formData.append('notes', data.notes ?? '');
            if (data.status != null && data.status !== '') {
                formData.append('status', data.status);
            }
            if (data.failureReason) {
                formData.append('failureReason', data.failureReason);
            }
            if (data.startAt) {
                formData.append('startAt', data.startAt);
            }
            if (data.endAt) {
                formData.append('endAt', data.endAt);
            }
            formData.append('document', file);
            const response = await api.post(`/jobs/${id}/progress`, formData, {
                transformRequest: [
                    (payload, headers) => {
                        if (payload instanceof FormData) {
                            delete headers['Content-Type'];
                        }
                        return payload;
                    }
                ]
            });
            return response.data;
        }
        const response = await api.post(`/jobs/${id}/progress`, data);
        return response.data;
    },

    // Update a progress report by createdAt (optional File as third arg — sent as multipart)
    updateProgressReport: async (id, data, file) => {
        if (file) {
            const formData = new FormData();
            formData.append('createdAt', data.createdAt ?? '');
            formData.append('notes', data.notes ?? '');
            if (data.startAt != null) formData.append('startAt', data.startAt);
            if (data.endAt != null) formData.append('endAt', data.endAt);
            if (data.totalTaken != null) formData.append('totalTaken', data.totalTaken);
            if (data.removeAttachment) formData.append('removeAttachment', 'true');
            if (data.workPeriods != null) {
                formData.append('workPeriods', JSON.stringify(data.workPeriods));
            }
            if (data.techEntries != null) {
                formData.append('techEntries', JSON.stringify(data.techEntries));
            }
            formData.append('document', file);
            const response = await api.put(`/jobs/${id}/progress`, formData, {
                transformRequest: [
                    (payload, headers) => {
                        if (payload instanceof FormData) {
                            delete headers['Content-Type'];
                        }
                        return payload;
                    }
                ]
            });
            return response.data;
        }
        const response = await api.put(`/jobs/${id}/progress`, data);
        return response.data;
    },

    // Delete a progress report by createdAt
    deleteProgressReport: async (id, createdAt) => {
        const response = await api.delete(`/jobs/${id}/progress`, {
            data: { createdAt }
        });
        return response.data;
    },

    // Get job files
    getJobFiles: async (id) => {
        const response = await api.get(`/jobs/${id}/files`);
        return response.data;
    },

    // Upload multiple files for a job
    uploadJobFiles: async (id, files, titles = []) => {
        const formData = new FormData();
        (files || []).forEach((file) => {
            formData.append('files', file);
        });
        formData.append('titles', JSON.stringify(titles || []));
        const response = await api.post(`/jobs/${id}/files`, formData, {
            transformRequest: [
                (payload, headers) => {
                    if (payload instanceof FormData) {
                        delete headers['Content-Type'];
                    }
                    return payload;
                }
            ]
        });
        return response.data;
    },

    // Update a job file title and/or replace the file
    updateJobFile: async (id, { fileId, path: filePath, title, file } = {}) => {
        const hasNewFile = Boolean(file);
        if (hasNewFile) {
            const formData = new FormData();
            if (fileId) formData.append('fileId', fileId);
            if (filePath) formData.append('path', filePath);
            if (title != null) formData.append('title', title);
            formData.append('file', file);
            const response = await api.put(`/jobs/${id}/files`, formData, {
                transformRequest: [
                    (payload, headers) => {
                        if (payload instanceof FormData) {
                            delete headers['Content-Type'];
                        }
                        return payload;
                    }
                ]
            });
            return response.data;
        }

        const response = await api.put(`/jobs/${id}/files`, {
            fileId,
            path: filePath,
            title
        });
        return response.data;
    },

    // Delete a job file by fileId or path
    deleteJobFile: async (id, payload) => {
        const response = await api.delete(`/jobs/${id}/files`, { data: payload });
        return response.data;
    },

    // Get delivery orders for job creation
    getDeliveryOrdersForJob: async () => {
        const response = await api.get('/jobs/delivery-orders');
        return response.data;
    },

    // Dispatch job
    dispatchJob: async (id, dispatchData) => {
        const response = await api.put(`/jobs/${id}/dispatch`, dispatchData);
        return response.data;
    },

    // Complete job
    completeJob: async (id, completeData) => {
        const response = await api.put(`/jobs/${id}/complete`, completeData);
        return response.data;
    }
};

export const surveyService = {
    getSurveys: async () => {
        const response = await api.get('/surveys');
        return response.data;
    },
    createSurvey: async (data) => {
        const response = await api.post('/surveys', data);
        return response.data;
    },
    updateSurvey: async (id, data) => {
        const response = await api.put(`/surveys/${id}`, data);
        return response.data;
    },
    deleteSurvey: async (id) => {
        const response = await api.delete(`/surveys/${id}`);
        return response.data;
    }
};

export const customerService = {
    // Get all customers
    getCustomers: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip);
        }
        if (params.search !== undefined && params.search !== '') {
            queryParams.append('search', params.search);
        }
        if (params.customerType !== undefined && params.customerType !== '') {
            queryParams.append('customerType', params.customerType);
        }
        const queryString = queryParams.toString();
        const url = queryString ? `/customers?${queryString}` : '/customers';
        const response = await api.get(url);
        return response.data;
    },

    // Get single customer
    getCustomer: async (id) => {
        const response = await api.get(`/customers/${id}`);
        return response.data;
    },

    getCustomerActivityLogs: async (id, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit != null) {
            queryParams.append('limit', params.limit);
        }
        const query = queryParams.toString();
        const url = query
            ? `/customers/${id}/activity-logs?${query}`
            : `/customers/${id}/activity-logs`;
        const response = await api.get(url);
        return response.data;
    },

    // Create new customer
    createCustomer: async (data) => {
        const response = await api.post('/customers', data);
        return response.data;
    },

    // Update customer
    updateCustomer: async (id, data) => {
        const response = await api.put(`/customers/${id}`, data);
        return response.data;
    },

    // Delete customer
    deleteCustomer: async (id) => {
        const response = await api.delete(`/customers/${id}`);
        return response.data;
    },

    // Add credit memo balance to a customer
    addCreditMemo: async (id, { amount, note } = {}) => {
        const response = await api.post(`/customers/${id}/credit-memo`, { amount, note });
        return response.data;
    },

    // Flag customer
    flagCustomer: async (id, flagReason) => {
        const response = await api.put(`/customers/${id}/flag`, { flagReason });
        return response.data;
    },

    // Unflag customer
    unflagCustomer: async (id) => {
        const response = await api.put(`/customers/${id}/flag`, { unflag: true });
        return response.data;
    },

    // Upload customer ID images
    uploadCustomerID: async (customerId, frontImage, backImage) => {
        const formData = new FormData();
        if (frontImage) {
            formData.append('frontImage', frontImage);
        }
        if (backImage) {
            formData.append('backImage', backImage);
        }
        const response = await api.post(`/customers/${customerId}/id-images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    // Check if customer has ID images
    checkCustomerID: async (customerId) => {
        const response = await api.get(`/customers/${customerId}/id-images`);
        return response.data;
    },

    // Get customer ID image URL
    getCustomerIDImageUrl: (customerId, type) => {
        return `${api.defaults.baseURL}/customers/${customerId}/id-images/${type}`;
    },

    // Delete customer ID image (front or back)
    deleteCustomerIDImage: async (customerId, type) => {
        const response = await api.delete(`/customers/${customerId}/id-images/${type}`);
        return response.data;
    },

    // Get branches for a customer
    getCustomerBranches: async (id) => {
        const response = await api.get(`/customers/${id}/branches`);
        return response.data;
    },

    // Merge duplicate customers into one with branches
    mergeCustomers: async (parentId, childIds) => {
        const response = await api.post('/customers/merge-branches', { parentId, childIds });
        return response.data;
    }
};

export const supplierService = {
    // Get all suppliers
    getSuppliers: async () => {
        const response = await api.get('/suppliers');
        return response.data;
    },

    // Create supplier
    createSupplier: async (supplierData) => {
        const response = await api.post('/suppliers', supplierData);
        return response.data;
    },

    // Update supplier
    updateSupplier: async (id, supplierData) => {
        const response = await api.put(`/suppliers/${id}`, supplierData);
        return response.data;
    },

    // Delete supplier
    deleteSupplier: async (id) => {
        const response = await api.delete(`/suppliers/${id}`);
        return response.data;
    }
};

export const bankAccountService = {
    // Get all bank accounts for the company
    getBankAccounts: async () => {
        const response = await api.get('/bank-accounts');
        return response.data;
    },

    // Get single bank account
    getBankAccount: async (id) => {
        const response = await api.get(`/bank-accounts/${id}`);
        return response.data;
    },

    // Create new bank account
    createBankAccount: async (data) => {
        const response = await api.post('/bank-accounts', data);
        return response.data;
    },

    // Update bank account
    updateBankAccount: async (id, data) => {
        const response = await api.put(`/bank-accounts/${id}`, data);
        return response.data;
    },

    // Delete bank account
    deleteBankAccount: async (id) => {
        const response = await api.delete(`/bank-accounts/${id}`);
        return response.data;
    },

    // Add balance to bank account
    addBalance: async (id, amount, description) => {
        const response = await api.put(`/bank-accounts/${id}/add-balance`, {
            amount,
            description
        });
        return response.data;
    },

    // Subtract balance from bank account
    subtractBalance: async (id, amount, description) => {
        const response = await api.put(`/bank-accounts/${id}/subtract-balance`, {
            amount,
            description
        });
        return response.data;
    }
};

export const csoService = {
    // Get unpicked jobs count (all jobs, not paginated)
    getUnpickedCount: async () => {
        const response = await api.get('/cso/stats/unpicked-count');
        return response.data;
    },
    
    // Get jobs in hand count (all jobs assigned to or picked up by current user, not paginated)
    getJobsInHandCount: async () => {
        const response = await api.get('/cso/stats/jobs-in-hand-count');
        return response.data;
    },

    /** CSOs whose repair_items include this inventory item (optional dateFrom/dateTo as YYYY-MM-DD) */
    getOrdersForInventoryItem: async (inventoryItemId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) queryParams.append('dateTo', params.dateTo);
        const query = queryParams.toString();
        const url = query
            ? `/cso/for-inventory-item/${encodeURIComponent(inventoryItemId)}?${query}`
            : `/cso/for-inventory-item/${encodeURIComponent(inventoryItemId)}`;
        const response = await api.get(url);
        return response.data;
    },

    // Get all CSOs with optional filters and pagination
    getCSOs: async (filters = {}, params = {}) => {
        const queryParams = new URLSearchParams();
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.customerId) queryParams.append('customerId', filters.customerId);
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.category) queryParams.append('category', filters.category);
        if (filters.employeeId) queryParams.append('employeeId', filters.employeeId);
        if (params.limit !== undefined) queryParams.append('limit', params.limit);
        if (params.skip !== undefined) queryParams.append('skip', params.skip);
        if (params.search) queryParams.append('search', params.search);
        
        const response = await api.get(`/cso?${queryParams.toString()}`);
        return response.data;
    },

    getCSOReportSummary: async (filters = {}) => {
        const queryParams = new URLSearchParams();
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.category) queryParams.append('category', filters.category);
        if (filters.employeeId) queryParams.append('employeeId', filters.employeeId);
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.customerId) queryParams.append('customerId', filters.customerId);
        const response = await api.get(`/cso/stats/report-summary?${queryParams.toString()}`);
        return response.data;
    },

    // Get single CSO by ID
    getCSO: async (id) => {
        const response = await api.get(`/cso/${id}`);
        return response.data;
    },

    // Get CSO by tracking ID
    getCSOByTrackingId: async (trackingId) => {
        const response = await api.get(`/cso/tracking/${trackingId}`);
        return response.data;
    },

    // Create new CSO
    createCSO: async (data) => {
        const response = await api.post('/cso', data);
        return response.data;
    },

    // Update CSO
    updateCSO: async (id, data) => {
        const response = await api.put(`/cso/${id}`, data);
        return response.data;
    },

    // Delete CSO
    deleteCSO: async (id) => {
        const response = await api.delete(`/cso/${id}`);
        return response.data;
    },

    // Mark CSO as picked up
    pickupCSO: async (id, data = {}) => {
        const response = await api.put(`/cso/${id}/pickup`, data);
        return response.data;
    },

    // Mark CSO as released
    releaseCSO: async (id, releaseData) => {
        const response = await api.put(`/cso/${id}/release`, releaseData);
        return response.data;
    },

    // Record payment for CSO
    recordPayment: async (id, paymentData) => {
        const response = await api.put(`/cso/${id}/payment`, paymentData);
        return response.data;
    },

    // Record refund for CSO
    recordRefund: async (id, refundData) => {
        const response = await api.put(`/cso/${id}/refund`, refundData);
        return response.data;
    },

    // Mark CSO as denied by customer
    denyCSO: async (id, reason) => {
        const response = await api.put(`/cso/${id}/deny`, { denialReason: reason });
        return response.data;
    },

    // Mark CSO as completed (optional repairConsumptions: parallel array to CSO repairItems, each { serialNumbers?: string[] })
    completeCSO: async (id, jobDone = null, repairConsumptions = null) => {
        const payload = {};
        if (jobDone !== null) {
            payload.jobDone = jobDone;
        }
        if (repairConsumptions != null) {
            payload.repairConsumptions = repairConsumptions;
        }
        const response = await api.put(`/cso/${id}/complete`, payload);
        return response.data;
    },

    // Get comments for a CSO
    getComments: async (id) => {
        const response = await api.get(`/cso/${id}/comments`);
        return response.data;
    },

    // Add comment to CSO
    addComment: async (id, commentText) => {
        const response = await api.post(`/cso/${id}/comments`, { text: commentText });
        return response.data;
    },

    // Upload signed CSO document (completed orders only)
    uploadSignedDocument: async (id, file) => {
        const formData = new FormData();
        formData.append('document', file);
        const response = await api.post(`/cso/${id}/signed-document`, formData, {
            transformRequest: [
                (payload, headers) => {
                    if (payload instanceof FormData) {
                        delete headers['Content-Type'];
                    }
                    return payload;
                }
            ]
        });
        return response.data;
    },

    // Delete signed CSO document
    deleteSignedDocument: async (id) => {
        const response = await api.delete(`/cso/${id}/signed-document`);
        return response.data;
    }
};

/** Staff: customer portal complaints (company-scoped, x-auth-token). */
export const staffComplaintService = {
    list: async (params = {}) => {
        const response = await api.get('/complaints', { params });
        return response.data;
    },
    get: async (id) => {
        const response = await api.get(`/complaints/${id}`);
        return response.data;
    },
    patch: async (id, body) => {
        const response = await api.patch(`/complaints/${id}`, body);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/complaints/${id}`);
        return response.data;
    },
    /** Triggers browser download using authenticated GET (blob). */
    downloadAttachment: async (complaintId, attachmentId, originalName) => {
        const response = await api.get(
            `/complaints/${complaintId}/attachments/${attachmentId}/file`,
            { responseType: 'blob' }
        );
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalName || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    },
    /** Authenticated file fetch for inline preview (caller revokes object URL when done). */
    getAttachmentBlob: async (complaintId, attachmentId) => {
        const response = await api.get(
            `/complaints/${complaintId}/attachments/${attachmentId}/file`,
            { responseType: 'blob' }
        );
        const blob = response.data;
        const headerType = response.headers['content-type'];
        const mime = (headerType && headerType.split(';')[0].trim()) || blob.type || 'application/octet-stream';
        const typedBlob =
            blob.type && blob.type !== 'application/octet-stream'
                ? blob
                : new Blob([blob], { type: mime });
        return { blob: typedBlob, contentType: mime };
    }
};

// Position Service
export const positionService = {
    // Get all positions
    getPositions: async () => {
        const response = await api.get('/positions');
        return response.data;
    },

    // Create a new position
    createPosition: async (data) => {
        const response = await api.post('/positions', data);
        return response.data;
    },

    // Update a position
    updatePosition: async (id, data) => {
        const response = await api.put(`/positions/${id}`, data);
        return response.data;
    },

    // Delete a position
    deletePosition: async (id) => {
        const response = await api.delete(`/positions/${id}`);
        return response.data;
    },

    // Get privileges for a position
    getPositionPrivileges: async (positionId) => {
        const response = await api.get(`/positions/${positionId}/privileges`);
        return response.data;
    },

    // Update privileges for a position
    updatePositionPrivileges: async (positionId, privileges) => {
        const response = await api.put(`/positions/${positionId}/privileges`, { privileges });
        return response.data;
    }
};

// Department Service
export const departmentService = {
    // Get all departments
    getDepartments: async () => {
        const response = await api.get('/departments');
        return response.data;
    },

    // Create a new department
    createDepartment: async (data) => {
        const response = await api.post('/departments', data);
        return response.data;
    },

    // Update a department
    updateDepartment: async (id, data) => {
        const response = await api.put(`/departments/${id}`, data);
        return response.data;
    },

    // Delete a department
    deleteDepartment: async (id) => {
        const response = await api.delete(`/departments/${id}`);
        return response.data;
    }
};

// Contract Type Service
export const contractTypeService = {
    // Get all contract types
    getContractTypes: async () => {
        const response = await api.get('/contract-types');
        return response.data;
    },

    // Create a new contract type
    createContractType: async (data) => {
        const response = await api.post('/contract-types', data);
        return response.data;
    },

    // Update a contract type
    updateContractType: async (id, data) => {
        const response = await api.put(`/contract-types/${id}`, data);
        return response.data;
    },

    // Delete a contract type
    deleteContractType: async (id) => {
        const response = await api.delete(`/contract-types/${id}`);
        return response.data;
    }
};

// Expense Type Service
export const expenseTypeService = {
    // Get all expense types
    getExpenseTypes: async () => {
        const response = await api.get('/expense-types');
        return response.data;
    },

    // Create a new expense type
    createExpenseType: async (data) => {
        const response = await api.post('/expense-types', data);
        return response.data;
    },

    // Update an expense type
    updateExpenseType: async (id, data) => {
        const response = await api.put(`/expense-types/${id}`, data);
        return response.data;
    },

    // Delete an expense type
    deleteExpenseType: async (id) => {
        const response = await api.delete(`/expense-types/${id}`);
        return response.data;
    }
};

// Customer Type Service
export const customerTypeService = {
    getCustomerTypes: async () => {
        const response = await api.get('/customer-types');
        return response.data;
    },
    createCustomerType: async (data) => {
        const response = await api.post('/customer-types', data);
        return response.data;
    },
    updateCustomerType: async (id, data) => {
        const response = await api.put(`/customer-types/${id}`, data);
        return response.data;
    },
    deleteCustomerType: async (id) => {
        const response = await api.delete(`/customer-types/${id}`);
        return response.data;
    }
};

export const activityLogService = {
    getActivityLogs: async (params = {}) => {
        const response = await api.get('/activity-logs', { params });
        return response.data;
    }
};

export default api; 